// Offering a clinical exam station (OSCE practice) in the middle of a review
// (owner's plan, part 7). When an answer takes the learner across the
// station's mark on the progress bar and they press "next", and one of the
// sets being reviewed has an examination they are due to sit, the station
// opens full screen on its door, or on "beat your last run" when there is a
// score to beat. The learner can enter it or turn it down; the review waits
// underneath.
//
// Rules (owner, 2026-09-28): offered at most once per review session, and
// turning it down keeps it due (it comes back the next day).
//
// The review's navigation code calls this, and that code is part of the app's
// startup. So this file only looks up an answer the review page worked out
// beforehand (clinicalExamStationsDueInReview.ts); it must not import the
// examination catalog.

import { trackEvent } from "../backend/utils/helper-methods";
import type { CardData, UserData } from "../constants/GlobalTypesAndEnumsAndConstants";
import { useClinicalExamStationStore } from "../zustand/clinicalExamStationStore";
import { isClinicalExamPracticeEnabledForUser } from "./clinicalExamPracticeAvailability";

// The sets of these cards, each once, in the order they first appear.
export function getDeckIdsOfCards(cards: CardData[] | null | undefined): string[] {
  const deckIds: string[] = [];
  for (const card of cards ?? []) {
    const deckId = card?.deck_id;
    if (typeof deckId === "string" && deckId && !deckIds.includes(deckId)) deckIds.push(deckId);
  }
  return deckIds;
}

// Opens a station over the review when one is due. Returns whether it did.
export function maybeOfferClinicalExamStationInReview({
  user,
  cardsReviewedSoFar,
}: {
  user: UserData | null | undefined;
  cardsReviewedSoFar: CardData[];
}): boolean {
  if (!isClinicalExamPracticeEnabledForUser(user)) return false;
  const stationStoreState = useClinicalExamStationStore.getState();
  if (stationStoreState.hasOfferedStationInThisReviewSession || stationStoreState.openStation !== null) return false;

  // The set the learner has just been answering questions from comes first.
  const deckIdsMostRecentFirst = getDeckIdsOfCards([...cardsReviewedSoFar].reverse());
  for (const deckId of deckIdsMostRecentFirst) {
    const stationDue = stationStoreState.stationsDueInReviewByDeckId[deckId];
    if (!stationDue) continue;

    stationStoreState.markStationOfferedInThisReviewSession();
    stationStoreState.openClinicalExamStation({
      clinicalExamId: stationDue.clinicalExamId,
      deck: { id: deckId, name: stationDue.deckName },
      entryPoint: "review",
    });
    void trackEvent(
      "clinical exam station offered in review",
      {
        exam_id: stationDue.clinicalExamId,
        deck_id: deckId,
        offer_kind: stationDue.offerKind,
        last_score_percent: stationDue.lastScorePercent,
      },
      undefined,
      null
    );
    return true;
  }
  return false;
}
